/**
 * GLSL of the baked light volume (lifted from the shared prototype engine's
 * `engine/src/radiance.js` hook and the dungeon-floors prototype's
 * `dungeon-radiance.js` adapter, whose `beforeDaylight` and `afterBake` slot
 * bodies are the `daylight` and `capTopUp` strings below). Composed once by
 * `radianceHook` in `radiance.ts`; rock-only code is under `#ifdef
 * DUNGEON_ROCK`, which the hook defines for rock programs.
 */

export const radianceVertexHeader = 'varying vec3 hdLightPosition;';

/** After `worldpos_vertex`: the world position, per instance. */
export const radianceVertex = `
vec4 hdLightVertex=vec4(transformed,1.);
#ifdef USE_INSTANCING
  hdLightVertex=instanceMatrix*hdLightVertex;
#endif
hdLightPosition=(modelMatrix*hdLightVertex).xyz;`;

export const radianceFragmentHeader = `
uniform highp sampler3D hdRadiance;uniform vec3 hdRadianceMin,hdRadianceSpan;varying vec3 hdLightPosition;
uniform sampler2D dungeonExplored;uniform vec2 dungeonFloorSize;
uniform float dungeonUnknownRockGain,dungeonUnknownMoon,dungeonCapSide,dungeonFogDesat,dungeonEdgeMoon;
uniform vec3 dungeonUnknownLight,dungeonFogAlbedo;uniform vec4 dungeonCapAmbient;
// Unexplored share (1 in fog) for the surface programs; outside the floor counts as unexplored.
float dqFogAt(vec3 p){vec2 uv=(vec2(p.x,-p.z)+.5)/dungeonFloorSize;return 1.-texture2D(dungeonExplored,uv).r*step(0.,uv.x)*step(uv.x,1.)*step(0.,uv.y)*step(uv.y,1.);}`;

/**
 * Before `lights_physical_fragment` on ramp surfaces: an unexplored ramp
 * colour loses some chroma, then leans toward the fog hue (the smooth fog of
 * war: the exploration mask is bilinear, so fog edges are one tile soft).
 */
export const rampFog = `
{float fogSeen=1.-dqFogAt(hdLightPosition);
diffuseColor.rgb=mix(diffuseColor.rgb,vec3(dot(diffuseColor.rgb,vec3(.2126,.7152,.0722))),dungeonFogDesat*(1.-fogSeen));
diffuseColor.rgb*=mix(dungeonFogAlbedo,vec3(1.),fogSeen);}`;

/**
 * The rock-top ambient floor: rock above the floor slab never falls below the
 * cap ambient light scaled by the mask's B share (border rock eases to the far
 * share outside the grid). Up-facing tops take all of it, steep faces the
 * side share.
 */
const capFloor = `
vec3 capFloor;{vec2 capUv=(vec2(hdLightPosition.x,-hdLightPosition.z)+.5)/dungeonFloorSize;float capIn=exp(-length(max(max(-capUv,capUv-1.),0.)*dungeonFloorSize)*.8);
  float capShare=mix(dungeonCapAmbient.w,tileMask.b,capIn),capGate=smoothstep(.1,.2,hdLightPosition.y)*mix(dungeonCapSide,1.,smoothstep(.45,.75,normalize(dqNormal).y));
  capFloor=dungeonCapAmbient.rgb*(capShare*capGate);}`;

/** The dungeon's remap of the fetched light: edge moon outside the volume, unexplored light and moon, the cap floor. */
const daylight = `
bakedLight.a=mix(dungeonEdgeMoon,bakedLight.a,inVolume);
vec3 tileMask=texture2D(dungeonExplored,(vec2(hdLightPosition.x,-hdLightPosition.z)+.5)/dungeonFloorSize).rgb;float explored=tileMask.r*inVolume;
bakedLight.rgb=mix(dungeonUnknownLight*mix(1.,dungeonUnknownRockGain,tileMask.g)/3.,bakedLight.rgb,explored);
bakedLight.a*=mix(dungeonUnknownMoon,1.,explored);
#ifdef DUNGEON_ROCK
${capFloor}
#endif
inVolume=1.;`;

/** After the baked light is added to the indirect light: rock tops up to the cap floor. */
const capTopUp = `
#ifdef DUNGEON_ROCK
reflectedLight.indirectDiffuse+=max(diffuseColor.rgb*capFloor-reflectedLight.directDiffuse-reflectedLight.indirectDiffuse,0.);
#endif`;

/** After `lights_fragment_end`: moon share scales direct light, baked RGB adds indirect light. */
export const radianceLighting = `
vec3 lightUVW=(hdLightPosition-hdRadianceMin)/hdRadianceSpan;
float inVolume=step(0.,lightUVW.x)*step(lightUVW.x,1.)*step(0.,lightUVW.y)*step(lightUVW.y,1.)*step(0.,lightUVW.z)*step(lightUVW.z,1.);
vec4 bakedLight=vec4(0.,0.,0.,1.);
if(inVolume>.5) bakedLight=texture(hdRadiance,lightUVW);
${daylight}
float daylight=mix(1.,bakedLight.a,inVolume);
reflectedLight.directDiffuse*=daylight;
reflectedLight.indirectDiffuse*=mix(1.,.23,1.-daylight);
reflectedLight.directSpecular*=daylight;
reflectedLight.indirectDiffuse+=diffuseColor.rgb*bakedLight.rgb*3.*inVolume;
${capTopUp}`;
